import React, { useMemo } from "react";
import CloseIcon from "@mui/icons-material/Close";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

import { PLATFORM_ENUM_OPTIONS } from "constants/coupons";
import { PLATFORM_META } from "constants/platformUi";

import packageStyles from "../PackageSelector/PackageSelector.module.scss";
import styles from "./PlatformSelector.module.scss";

const PlatformSelector = ({
  selectAll = false,
  selectedIds = [],
  onChange,
  disabled = false,
  error,
}) => {
  const platforms = PLATFORM_ENUM_OPTIONS;

  const selectedPlatforms = useMemo(
    () =>
      selectedIds
        .map((id) => platforms.find((platform) => platform.platformId === id))
        .filter(Boolean),
    [platforms, selectedIds]
  );

  const handleApplyModeChange = (mode) => {
    if (disabled) return;
    if (mode === "all") {
      onChange({ selectAll: true, platformIds: [] });
      return;
    }
    onChange({ selectAll: false, platformIds: selectedIds });
  };

  const togglePlatform = (platformId) => {
    if (disabled || selectAll) return;

    const nextSelected = selectedIds.includes(platformId)
      ? selectedIds.filter((id) => id !== platformId)
      : [...selectedIds, platformId];

    const allPlatformIds = platforms.map((platform) => platform.platformId);
    const hasAllSelected =
      allPlatformIds.length > 0 &&
      allPlatformIds.every((id) => nextSelected.includes(id));

    if (hasAllSelected) {
      onChange({ selectAll: true, platformIds: [] });
      return;
    }

    onChange({ selectAll: false, platformIds: nextSelected });
  };

  const removePlatform = (platformId) => {
    if (disabled) return;
    onChange({
      selectAll: false,
      platformIds: selectedIds.filter((id) => id !== platformId),
    });
  };

  const clearAll = () => {
    if (disabled) return;
    onChange({ selectAll: false, platformIds: [] });
  };

  const isPlatformSelected = (platformId) => selectedIds.includes(platformId);

  return (
    <div className={styles.container}>
      <div className={styles.applyRow}>
        <span className={styles.applyLabel}>Apply to</span>
        <div className={styles.radioGroup}>
          <label className={styles.radioOption}>
            <input
              type="radio"
              name="platformApplyMode"
              checked={selectAll}
              onChange={() => handleApplyModeChange("all")}
              disabled={disabled}
            />
            <span>All Platforms</span>
          </label>
          <label className={styles.radioOption}>
            <input
              type="radio"
              name="platformApplyMode"
              checked={!selectAll}
              onChange={() => handleApplyModeChange("selected")}
              disabled={disabled}
            />
            <span>Selected Platforms</span>
          </label>
        </div>
      </div>

      <div className={packageStyles.selectionPanel}>
        {selectAll ? (
          <div className={packageStyles.selectionPanelAll}>
            <div className={styles.infoBanner}>
              <InfoOutlinedIcon fontSize="small" />
              <span>Coupon will be applicable to all platforms.</span>
            </div>
          </div>
        ) : (
          <>
          <div className={`${packageStyles.toolbar} ${packageStyles.toolbarEnd}`}>
            <span className={packageStyles.clearAllSlot}>
              <button
                type="button"
                className={packageStyles.clearAllBtn}
                onClick={clearAll}
                disabled={disabled || selectedIds.length === 0}
                style={{ visibility: selectedIds.length > 0 ? "visible" : "hidden" }}
              >
                Clear All
              </button>
            </span>
          </div>

          <div className={packageStyles.dualPane}>
            <div className={packageStyles.browsePane}>
              <div className={packageStyles.paneHeader}>
                <h3>Browse Platforms</h3>
                <span>{platforms.length} available</span>
              </div>
              <div className={styles.browseList}>
                {platforms.map((platform) => {
                  const meta = PLATFORM_META[platform.key] ?? {};
                  const PlatformIcon = meta.icon;
                  const isSelected = isPlatformSelected(platform.platformId);

                  return (
                    <label
                      key={platform.key}
                      className={`${styles.platformCard} ${
                        isSelected ? styles.platformCardSelected : ""
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => togglePlatform(platform.platformId)}
                        disabled={disabled}
                      />
                      <div className={styles.platformCardContent}>
                        <div className={styles.platformCardTitle}>
                          {PlatformIcon && (
                            <PlatformIcon className={styles.platformIcon} />
                          )}
                          <span>{platform.label}</span>
                        </div>
                        {meta.description && (
                          <span className={styles.platformDescription}>
                            {meta.description}
                          </span>
                        )}
                      </div>
                    </label>
                  );
                })}
              </div>
            </div>

            <div className={packageStyles.selectedPane}>
              <div className={packageStyles.paneHeader}>
                <h3>Selected Platforms ({selectedPlatforms.length})</h3>
              </div>
              <div className={packageStyles.selectedList}>
                {selectedPlatforms.length === 0 ? (
                  <p className={packageStyles.stateText}>
                    No platforms selected yet.
                  </p>
                ) : (
                  selectedPlatforms.map((platform) => {
                    const meta = PLATFORM_META[platform.key] ?? {};
                    return (
                      <div
                        key={platform.platformId}
                        className={packageStyles.selectedItem}
                      >
                        <div>
                          <span className={packageStyles.selectedName}>
                            {platform.label}
                          </span>
                          {meta.description && (
                            <span className={packageStyles.selectedMeta}>
                              {meta.description}
                            </span>
                          )}
                        </div>
                        <button
                          type="button"
                          className={packageStyles.selectedRemove}
                          onClick={() => removePlatform(platform.platformId)}
                          disabled={disabled}
                          aria-label={`Remove ${platform.label}`}
                        >
                          <CloseIcon fontSize="small" />
                        </button>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </div>

          <div className={styles.infoBanner}>
            <InfoOutlinedIcon fontSize="small" />
            <span>Coupon will be applicable only to the selected platforms.</span>
          </div>
          </>
        )}
      </div>

      {error && <p className={styles.fieldError}>{error}</p>}
    </div>
  );
};

export default PlatformSelector;
